/** @jsx jsx */
import { css, jsx, SerializedStyles } from "@emotion/core"
import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from "react"

declare module "react" {
  interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
    popover?: "auto" | "manual" | ""
    onToggle?: (event: ToggleEvent) => void
  }

  // Add CSS Anchor positioning properties here
  interface CSSProperties {
    anchorName?: string
    positionAnchor?: string
    positionArea?: string
  }
}

export type PopoverRef = {
  toggle(): void
  isVisible(): boolean
  hide(): void
  show(): void
}

type Placement =
  | "bottom"
  | "top"
  | "left"
  | "right"
  | "bottom-left"
  | "bottom-right"
  | "top-left"
  | "top-right"

type FixedCoords = {
  top: number
  left: number
  maxHeight: number
}

type PlacementResult = {
  placement: Placement
  fixedCoords: FixedCoords | null
}

type PopoverProps = {
  getContent: () => React.ReactNode
  children: React.ReactNode
  lazy?: boolean
  trigger?: "click" | "hover" | "none"
  onShow?: () => void
  onHidden?: () => void
  className?: string
  offset?: [number, number]
  delay?: [number, number]
  visible?: boolean
  interactive?: boolean
  placement?: Placement
  maxWidth?: number | null
  inline?: boolean
  contentCss?: SerializedStyles
  animation?: "fade" | "none"
}

const VIEWPORT_MARGIN = 8

const placementMap: Record<Placement, string> = {
  bottom: "bottom center",
  "bottom-left": "bottom span-right",
  "bottom-right": "bottom span-left",
  top: "top center",
  "top-left": "top span-right",
  "top-right": "top span-left",
  left: "left center",
  right: "right center",
}

function flipBlock(placement: Placement): Placement {
  switch (placement) {
    case "bottom":
      return "top"
    case "top":
      return "bottom"
    case "bottom-left":
      return "top-left"
    case "top-left":
      return "bottom-left"
    case "bottom-right":
      return "top-right"
    case "top-right":
      return "bottom-right"
    default:
      return placement
  }
}

function flipInline(placement: Placement): Placement {
  switch (placement) {
    case "bottom-left":
      return "bottom-right"
    case "bottom-right":
      return "bottom-left"
    case "top-left":
      return "top-right"
    case "top-right":
      return "top-left"
    case "left":
      return "right"
    case "right":
      return "left"
    default:
      return placement
  }
}

function computeFixedCoords(
  anchor: DOMRect,
  popoverSize: { width: number; height: number },
): FixedCoords {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const maxHeight = Math.max(VIEWPORT_MARGIN * 2, vh - VIEWPORT_MARGIN * 2)
  const height = Math.min(popoverSize.height, maxHeight)
  const width = Math.min(popoverSize.width, vw - VIEWPORT_MARGIN * 2)

  // Center vertically in the viewport; keep horizontally near the anchor when possible.
  const top = Math.max(VIEWPORT_MARGIN, (vh - height) / 2)
  const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.left, vw - width - VIEWPORT_MARGIN))

  return { top, left, maxHeight }
}

/** Pick a placement that keeps the popover inside the viewport when possible. */
function computeFittingPlacement(
  preferred: Placement,
  anchor: DOMRect,
  popoverSize: { width: number; height: number },
  offsetX: number,
  offsetY: number,
): PlacementResult {
  const vw = window.innerWidth
  const vh = window.innerHeight
  let next = preferred

  const spaceBelow = vh - anchor.bottom - offsetY
  const spaceAbove = anchor.top - offsetY
  const spaceRight = vw - anchor.left - offsetX
  const spaceLeft = anchor.right - offsetX

  const fitsBelow = spaceBelow >= popoverSize.height + VIEWPORT_MARGIN
  const fitsAbove = spaceAbove >= popoverSize.height + VIEWPORT_MARGIN
  const fitsRight = spaceRight >= popoverSize.width + VIEWPORT_MARGIN
  const fitsLeft = spaceLeft >= popoverSize.width + VIEWPORT_MARGIN

  // Block axis: prefer → flip → fixed (neither side fits)
  if (next === "bottom" || next === "bottom-left" || next === "bottom-right") {
    if (fitsBelow) {
      // keep preferred
    } else if (fitsAbove) {
      next = flipBlock(next)
    } else {
      return { placement: next, fixedCoords: computeFixedCoords(anchor, popoverSize) }
    }
  } else if (next === "top" || next === "top-left" || next === "top-right") {
    if (fitsAbove) {
      // keep preferred
    } else if (fitsBelow) {
      next = flipBlock(next)
    } else {
      return { placement: next, fixedCoords: computeFixedCoords(anchor, popoverSize) }
    }
  }

  // Inline axis flip (still anchored)
  if (next === "left") {
    if (!fitsLeft && fitsRight) next = flipInline(next)
  } else if (next === "right") {
    if (!fitsRight && fitsLeft) next = flipInline(next)
  } else if (next === "bottom-left" || next === "top-left") {
    if (!fitsRight && fitsLeft) next = flipInline(next)
  } else if (next === "bottom-right" || next === "top-right") {
    if (!fitsLeft && fitsRight) next = flipInline(next)
  }

  return { placement: next, fixedCoords: null }
}

const containsTarget = (container: HTMLElement | null, target: EventTarget | null) =>
  target instanceof Node && !!container?.contains(target)

const isTextEntryTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  if (tag === "TEXTAREA" || tag === "SELECT") return true
  if (tag !== "INPUT") return false
  const type = (target as HTMLInputElement).type
  return (
    type !== "button" &&
    type !== "submit" &&
    type !== "reset" &&
    type !== "checkbox" &&
    type !== "radio"
  )
}

function fixedCoordsEqual(a: FixedCoords | null, b: FixedCoords | null) {
  if (a === b) return true
  if (!a || !b) return false
  return a.top === b.top && a.left === b.left && a.maxHeight === b.maxHeight
}

export const Popover = forwardRef<PopoverRef, PopoverProps>(
  (
    {
      getContent,
      children,
      lazy = false,
      trigger = "click",
      onShow,
      onHidden,
      className,
      offset = [0, 0],
      delay = [0, 0],
      visible,
      interactive = true,
      placement,
      maxWidth,
      inline = false,
      contentCss,
      animation = "fade",
    },
    ref,
  ) => {
    const popoverRef = useRef<HTMLElement>(null)
    const anchorRef = useRef<HTMLDivElement>(null)
    const showTimeoutRef = useRef<number | null>(null)
    const hideTimeoutRef = useRef<number | null>(null)
    const [isOpen, setIsOpen] = useState(visible === true)
    const preferredPlacement = placement ?? "bottom"
    const [effectivePlacement, setEffectivePlacement] = useState<Placement>(preferredPlacement)
    const [fixedCoords, setFixedCoords] = useState<FixedCoords | null>(null)
    const { current: id } = useRef(`popover-${Math.random().toString(36).substring(2, 11)}`)

    useImperativeHandle(ref, () => ({
      toggle() {
        if (popoverRef.current?.matches(":popover-open")) {
          popoverRef.current.hidePopover()
        } else {
          setIsOpen(true)
        }
      },
      isVisible() {
        return isOpen
      },
      hide() {
        setIsOpen(false)
      },
      show() {
        setIsOpen(true)
      },
    }))

    useEffect(() => {
      if (visible !== undefined) {
        setIsOpen(visible)
      }
    }, [visible])

    // Flip / fixed-fallback when the preferred side would overflow the viewport.
    useEffect(() => {
      if (!isOpen) {
        setEffectivePlacement(preferredPlacement)
        setFixedCoords(null)
        return
      }

      const popoverEl = popoverRef.current
      const anchorEl = anchorRef.current
      if (!popoverEl || !anchorEl) return

      const update = () => {
        // Use scrollHeight so a max-height clamp in fixed mode doesn't make us
        // think the content suddenly fits beside the anchor again.
        const size = {
          width: Math.max(popoverEl.offsetWidth, popoverEl.scrollWidth),
          height: Math.max(popoverEl.offsetHeight, popoverEl.scrollHeight),
        }
        const result = computeFittingPlacement(
          preferredPlacement,
          anchorEl.getBoundingClientRect(),
          size,
          offset[0],
          offset[1],
        )
        setEffectivePlacement(current =>
          current === result.placement ? current : result.placement,
        )
        setFixedCoords(current =>
          fixedCoordsEqual(current, result.fixedCoords) ? current : result.fixedCoords,
        )
      }

      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(update)
      })
      const resizeObserver = new ResizeObserver(update)
      resizeObserver.observe(popoverEl)
      window.addEventListener("resize", update)
      window.addEventListener("scroll", update, true)

      return () => {
        cancelAnimationFrame(raf)
        resizeObserver.disconnect()
        window.removeEventListener("resize", update)
        window.removeEventListener("scroll", update, true)
      }
    }, [isOpen, preferredPlacement, offset[0], offset[1]])

    useEffect(() => {
      const popoverEl = popoverRef.current
      if (!popoverEl) return

      try {
        if (isOpen) {
          if (!popoverEl.matches(":popover-open")) {
            popoverEl.showPopover()
          }
        } else if (popoverEl.matches(":popover-open")) {
          popoverEl.hidePopover()
        }
      } catch (e) {
        console.error(e)
      }
    }, [isOpen])

    useEffect(() => {
      const popoverEl = popoverRef.current
      if (!popoverEl) return

      const handleEscapeKey = (e: KeyboardEvent) => {
        if (e.key !== "Escape") return

        const openPopovers = document.querySelectorAll(":popover-open")
        const topmost = openPopovers[openPopovers.length - 1]
        if (topmost !== popoverEl) return

        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        try {
          popoverEl.hidePopover()
        } catch (err) {
          console.error(err)
        }
      }

      let listening = false

      const syncEscapeListener = () => {
        const isOpen = popoverEl.matches(":popover-open")
        if (isOpen && !listening) {
          document.addEventListener("keydown", handleEscapeKey, true)
          listening = true
        } else if (!isOpen && listening) {
          document.removeEventListener("keydown", handleEscapeKey, true)
          listening = false
        }
      }

      const handleToggleForEscape = () => syncEscapeListener()

      popoverEl.addEventListener("toggle", handleToggleForEscape)
      syncEscapeListener()

      return () => {
        popoverEl.removeEventListener("toggle", handleToggleForEscape)
        document.removeEventListener("keydown", handleEscapeKey, true)
      }
    }, [])

    const clearTimers = () => {
      if (showTimeoutRef.current !== null) {
        clearTimeout(showTimeoutRef.current)
        showTimeoutRef.current = null
      }
      if (hideTimeoutRef.current !== null) {
        clearTimeout(hideTimeoutRef.current)
        hideTimeoutRef.current = null
      }
    }

    const handleOpen = () => {
      clearTimers()
      showTimeoutRef.current = window.setTimeout(() => {
        showTimeoutRef.current = null
        setIsOpen(true)
      }, delay[0])
    }

    const handleClose = () => {
      clearTimers()
      hideTimeoutRef.current = window.setTimeout(() => {
        hideTimeoutRef.current = null
        setIsOpen(false)
      }, delay[1])
    }

    useEffect(() => {
      const popoverEl = popoverRef.current
      if (!popoverEl) return

      const handleNativeToggle = (event: Event) => {
        const e = event as ToggleEvent
        const isNowOpen = e.newState === "open"
        setIsOpen(isNowOpen)
        if (isNowOpen) {
          onShow?.()
          // React's autoFocus only runs on mount and does not set an HTML attribute.
          popoverEl.querySelector<HTMLElement>("[type='search']")?.focus()
        } else {
          onHidden?.()
        }
      }

      popoverEl.addEventListener("toggle", handleNativeToggle)
      return () => popoverEl.removeEventListener("toggle", handleNativeToggle)
    }, [onShow, onHidden])

    const handleClick = () => {
      if (visible !== undefined) return
      clearTimers()
      if (trigger === "click") {
        setIsOpen(open => !open)
      } else {
        setIsOpen(true)
      }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (trigger !== "click") return
      if (e.key !== "Enter" && e.key !== " ") return
      if (isTextEntryTarget(e.target)) return
      e.preventDefault()
      handleClick()
    }

    const handleAnchorMouseLeave = (e: React.MouseEvent) => {
      if (trigger !== "hover") return
      if (interactive && containsTarget(popoverRef.current, e.relatedTarget)) return
      handleClose()
    }

    const handlePopoverMouseLeave = (e: React.MouseEvent) => {
      if (trigger !== "hover" || !interactive) return
      if (containsTarget(anchorRef.current, e.relatedTarget)) return
      handleClose()
    }

    const usesHover = trigger === "hover"
    const usesClick = trigger === "click"
    const usesTrigger = trigger !== "none"
    const useFixed = fixedCoords !== null

    return (
      <React.Fragment>
        <div
          ref={anchorRef}
          className="popover-anchor"
          onClick={usesTrigger && (usesClick || usesHover) ? handleClick : undefined}
          onKeyDownCapture={usesClick ? handleKeyDown : undefined}
          onMouseEnter={usesHover ? handleOpen : undefined}
          onMouseLeave={usesHover ? handleAnchorMouseLeave : undefined}
          onFocusCapture={usesHover ? handleOpen : undefined}
          onBlurCapture={usesHover ? handleClose : undefined}
          css={css`
            anchor-name: --anchor-${id};
            ${inline ? "display: inline-block;" : ""}
          `}
          tabIndex={usesTrigger ? 0 : undefined}
        >
          {children}
        </div>
        <div
          ref={popoverRef as React.RefObject<HTMLDivElement>}
          id={id}
          popover="auto"
          className={className}
          onMouseEnter={usesHover && interactive ? clearTimers : undefined}
          onMouseLeave={usesHover && interactive ? handlePopoverMouseLeave : undefined}
          style={
            useFixed && fixedCoords
              ? {
                  top: fixedCoords.top,
                  left: fixedCoords.left,
                  right: "auto",
                  bottom: "auto",
                  margin: 0,
                }
              : { positionAnchor: `--anchor-${id}` }
          }
          css={[
            css`
              ${useFixed && fixedCoords
                ? css`
                    position: fixed !important;
                    position-area: unset;
                    position-anchor: none;
                    max-height: ${fixedCoords.maxHeight}px;
                    overflow: auto;
                  `
                : css`
                    position-area: ${placementMap[effectivePlacement]};
                    margin: ${offset[1]}px ${offset[0]}px;
                  `}
              width: max-content;
              max-width: ${maxWidth !== undefined
                ? `min(${maxWidth}px, calc(100vw - 60px))`
                : "calc(100vw - 60px)"} !important;

              border: none;
              background: white;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
              padding: 0;
              border-radius: 4px;
              ${!useFixed ? "overflow: hidden;" : ""}

              /* Reset text styles */
              text-decoration: none;
              text-transform: none;
              text-align: left;
              text-indent: 0;
              text-overflow: clip;
              text-shadow: none;
              text-wrap: wrap;
              text-wrap-mode: wrap;
              font-weight: normal;
              font-style: normal;
              font-size: 14px;
              font-family: inherit;
              font-variant: normal;
              font-variant-numeric: normal;
              font-variant-ligatures: normal;
              font-variant-caps: normal;
              font-variant-east-asian: normal;

              /* Transitions */
              opacity: 0;
              transform: scale(0.95);
              transition: ${animation === "fade"
                ? css`
                    opacity 0.2s ease-out,
                    transform 0.2s ease-out,
                    display 0.2s ease-out allow-discrete,
                    overlay 0.2s ease-out allow-discrete
                  `
                : "none"};

              &:popover-open {
                opacity: 1;
                transform: scale(1);
              }

              @starting-style {
                &:popover-open {
                  opacity: 0;
                  transform: scale(0.95);
                }
              }
            `,
            contentCss,
          ]}
        >
          {lazy ? (isOpen ? getContent() : null) : getContent()}
        </div>
      </React.Fragment>
    )
  },
)

Popover.displayName = "Popover"
